import React from 'react';
import { mbcbMinistryPeriodCaption, mbcbReportNamedTitle } from '../../utils/mbcbReportPeriodLabels';

interface EditorShellProps {
  month: number;
  year: number;
  saveStatus: 'idle' | 'saving' | 'saved' | 'error';
  onBack: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  headerExtra?: React.ReactNode;
}

export const EditorShell: React.FC<EditorShellProps> = ({
  month,
  year,
  saveStatus,
  onBack,
  children,
  footer,
  headerExtra,
}) => (
  <div className="h-[100dvh] bg-gray-50 overscroll-none flex flex-col md:items-center md:justify-center md:bg-gray-100">
    <div className="flex flex-col h-full w-full md:max-w-2xl md:h-[95dvh] md:rounded-2xl md:shadow-xl md:overflow-hidden md:bg-white">
      <header className="shrink-0 bg-white border-b border-gray-200 pt-safe-top">
        <div className="flex items-center gap-2 px-2 py-2 min-h-[52px]">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full text-gray-700 hover:bg-gray-100 min-h-touch min-w-touch shrink-0"
            aria-label="Back to reports"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div className="flex-1 min-w-0 text-center px-1">
            <h1 className="text-sm font-semibold text-gray-900 truncate">
              {mbcbReportNamedTitle(month, year)}
            </h1>
            <p className="text-xs text-gray-500 truncate">{mbcbMinistryPeriodCaption(month, year)}</p>
          </div>
          <div className="flex items-center gap-1 shrink-0 w-16 justify-end">
            {saveStatus === 'saving' && (
              <span className="text-[10px] text-gray-500">Saving…</span>
            )}
            {saveStatus === 'saved' && (
              <span className="text-[10px] text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
                Saved
              </span>
            )}
            {saveStatus === 'error' && (
              <span className="text-[10px] text-red-600">Error</span>
            )}
            {headerExtra}
          </div>
        </div>
      </header>

      <div className="flex-1 min-h-0 flex flex-col overflow-hidden">{children}</div>

      {footer && (
        <div className="shrink-0 bg-white border-t border-gray-200 px-4 py-3 pb-safe-bottom">
          {footer}
        </div>
      )}
    </div>
  </div>
);

interface StickyNavProps {
  onBack?: () => void;
  onNext?: () => void;
  backLabel?: string;
  nextLabel?: string;
  nextDisabled?: boolean;
  nextVariant?: 'primary' | 'success';
}

export const StickyNav: React.FC<StickyNavProps> = ({
  onBack,
  onNext,
  backLabel = 'Back',
  nextLabel = 'Continue',
  nextDisabled = false,
  nextVariant = 'primary',
}) => (
  <div className="flex gap-3">
    {onBack && (
      <button
        type="button"
        onClick={onBack}
        className="flex-1 py-3 rounded-xl border border-gray-200 bg-white text-sm font-semibold text-gray-800 hover:bg-gray-50 min-h-touch"
      >
        {backLabel}
      </button>
    )}
    {onNext && (
      <button
        type="button"
        onClick={onNext}
        disabled={nextDisabled}
        className={`flex-[2] py-3 rounded-xl text-white text-sm font-semibold disabled:opacity-50 min-h-touch ${
          nextVariant === 'success'
            ? 'bg-emerald-600 hover:bg-emerald-700'
            : 'bg-primary-600 hover:bg-primary-700'
        }`}
      >
        {nextLabel}
      </button>
    )}
  </div>
);

/** Auto-growing textarea */
export const NarrativeField: React.FC<{
  label: string;
  hint?: string;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  rows?: number;
}> = ({ label, hint, value, onChange, disabled, rows = 3 }) => (
  <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm space-y-2">
    <label className="block text-sm font-semibold text-gray-900">{label}</label>
    {hint && <p className="text-xs text-gray-500 -mt-1">{hint}</p>}
    <textarea
      rows={rows}
      disabled={disabled}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="block w-full rounded-xl border-gray-200 text-sm resize-none focus:border-primary-500 focus:ring-primary-500 disabled:bg-gray-50"
    />
  </div>
);

export const AiAssistPill: React.FC<{ onClick: () => void; disabled?: boolean }> = ({
  onClick,
  disabled,
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="inline-flex items-center gap-2 rounded-full border border-primary-200 bg-primary-50 px-4 py-2 text-sm font-medium text-primary-800 hover:bg-primary-100 disabled:opacity-50 transition-colors"
  >
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
      />
    </svg>
    Talk it out
  </button>
);
